import { MenuItemDef } from '@ag-grid-community/core';
import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { AbstractControl, UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { Texts } from '@language/interfaces/interfaces';
import type { GetPcapPlayerFilesQuery } from '@uigy/generated/graphql';
import { PcapPlayerGridPageService } from '@uigy/modules/settings/modules/pcap-player/services/pcap-player-grid-page.service';
import { ToasterType } from '@uigy/shared/components/toaster/enums/toaster-type.enum';
import { ToasterService } from '@uigy/shared/components/toaster/services/toaster.service';
import { UploadFileComponent } from '@uigy/shared/components/upload-file/upload-file.component';
import { AppIdsEnum } from '@uigy/shared/directives/app-id/appIds.enum';
import { FlyoutSettings } from '@uigy/shared/models/interfaces';
import { ActionMenuItem } from '@uigy/shared/modules/grid/models/grid';
import { LanguageService } from '@uigy/shared/services/language.service';
import { Observable } from 'rxjs';

@Component({
    selector: 'app-pcap-player-grid-buttons',
    templateUrl: './pcap-player-grid-buttons.component.html',
    styleUrls: ['./pcap-player-grid-buttons.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class PcapPlayerGridButtonsComponent {

  @ViewChild('fileUpload') uploadFileRef!: UploadFileComponent;
  public isGridActionRunning$: Observable<boolean> = this.pcapPlayerGridPageService.isGridActionRunning$;
  public exportCsv$ = this.pcapPlayerGridPageService.exportCsv$;
  public actionsFlyoutSettings: FlyoutSettings<ActionMenuItem> = this.getFlyoutActions();
  public texts: Texts = this.languageService.texts;
  public form: UntypedFormGroup = this.fb.group({
    file: [null]
  });
  private _isRunning = false;
  private _focusedRow?: GetPcapPlayerFilesQuery['pcapPlayer']['files']['nodes'][number];

  @Input() set focusedRow(value: GetPcapPlayerFilesQuery['pcapPlayer']['files']['nodes'][number] | undefined) {
    this._focusedRow = value;
    this.actionsFlyoutSettings.appId = AppIdsEnum.pcap_btn_action,
      this.actionsFlyoutSettings.items = this.pcapPlayerGridPageService.getActionItems(this.focusedRow, this.isRunning);
    this.actionsFlyoutSettings.disable = !this.focusedRow;
  }

  get focusedRow(): GetPcapPlayerFilesQuery['pcapPlayer']['files']['nodes'][number] | undefined {
    return this._focusedRow;
  }

  @Input() set isRunning(value: boolean) {
    this._isRunning = value;
    this.actionsFlyoutSettings.disable = !this.focusedRow || this.isRunning;
  }

  get isRunning(): boolean {
    return this._isRunning;
  }

  @Output() actionSelect = new EventEmitter();
  @Output() uploadFile = new EventEmitter();

  constructor(private pcapPlayerGridPageService: PcapPlayerGridPageService,
              private fb: UntypedFormBuilder,
              private toasterService: ToasterService,
              private languageService: LanguageService) {
  }

  public openUploadDialog(): void {
    const fileInputHtmlElement = this.uploadFileRef.fileInputHtmlElement;
    fileInputHtmlElement.nativeElement.click();
  }

  public onFileChanged(): void {
    if (this.form.valid) {
      this.uploadFile.emit(this.form.get('file')!.value);
    } else {
      this.validateFileSize();
    }
  }

  public onSelect(flyoutData: MenuItemDef): void {
    flyoutData.action?.call(this, flyoutData.menuItemParams);
  }

  private validateFileSize(): void {
    const file: AbstractControl | null = this.form.get('file');
    const errors = file?.hasError('invalid_file_size');
    if (errors) {
      this.toasterService.openToaster({
        type: ToasterType.Error,
        message: file!.errors!.invalid_file_size
      });
    }
  }

  private getFlyoutActions(): FlyoutSettings<ActionMenuItem> {
    return {
      appId: AppIdsEnum.pcap_btn_action,
      labelKey: 'name',
      valueKey: 'action',
      items: [],
      disable: false,
      title: this.languageService.texts.general.flyout_title.actions,
    };
  }
}
